import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DateRange {
  since: Date;
  until: Date;
}

export type Preset = '7d' | '30d' | 'this-month' | 'last-month' | 'custom';

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function getPresetRange(preset: Preset): DateRange {
  const now = new Date();
  switch (preset) {
    case '7d':
      return { since: startOfDay(new Date(now.getTime() - 7 * 86_400_000)), until: now };
    case '30d':
      return { since: startOfDay(new Date(now.getTime() - 30 * 86_400_000)), until: now };
    case 'this-month':
      return { since: new Date(now.getFullYear(), now.getMonth(), 1), until: now };
    case 'last-month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { since: start, until: end };
    }
    default:
      return { since: startOfDay(new Date(now.getTime() - 30 * 86_400_000)), until: now };
  }
}

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function detectPreset(since: Date, until: Date): Preset {
  const now = new Date();
  const todayStr = toLocalDateString(now);
  const sinceStr = toLocalDateString(since);
  const untilStr = toLocalDateString(until);

  if (untilStr === todayStr) {
    const sevenDaysAgo = startOfDay(new Date(now.getTime() - 7 * 86_400_000));
    if (sinceStr === toLocalDateString(sevenDaysAgo)) return '7d';

    const thirtyDaysAgo = startOfDay(new Date(now.getTime() - 30 * 86_400_000));
    if (sinceStr === toLocalDateString(thirtyDaysAgo)) return '30d';

    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    if (sinceStr === toLocalDateString(thisMonthStart)) return 'this-month';
  }

  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
  if (sinceStr === toLocalDateString(lastMonthStart) && untilStr === toLocalDateString(lastMonthEnd)) return 'last-month';

  return 'custom';
}

export function useDateRange() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { since, until, preset } = useMemo(() => {
    const sinceParam = searchParams.get('since');
    const untilParam = searchParams.get('until');

    if (sinceParam && untilParam) {
      const s = new Date(sinceParam);
      const u = new Date(untilParam);
      if (!isNaN(s.getTime()) && !isNaN(u.getTime()) && s < u) {
        return { since: s, until: u, preset: detectPreset(s, u) };
      }
    }

    const defaultRange = getPresetRange('30d');
    return { ...defaultRange, preset: '30d' as Preset };
  }, [searchParams]);

  const setRange = useCallback(
    (newSince: Date, newUntil: Date) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('since', newSince.toISOString());
        next.set('until', newUntil.toISOString());
        return next;
      });
    },
    [setSearchParams],
  );

  const setPreset = useCallback(
    (p: Preset) => {
      const range = getPresetRange(p);
      setRange(range.since, range.until);
    },
    [setRange],
  );

  return { since, until, preset, setRange, setPreset };
}
